import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Globe2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PERIODS = [["month", "Ultimo mese"], ["quarter", "Ultimi 3 mesi"], ["year", "Ultimi 12 mesi"], ["calendar", "Anno solare"]];
const ZONES = [["italia", "🇮🇹 Italia", "text-green-700"], ["ue", "🇪🇺 UE (esclusa Italia)", "text-blue-700"], ["extra", "🌍 Extra UE", "text-amber-700"]];
const MONTHS = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
const eur = (v) => `€${Number(v || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** Jan→Dec table for one calendar year: revenue (orders) per zone + total. */
const CalendarYearTable = ({ monthly, year, years, onYear }) => {
  const sum = (z) => monthly.reduce((s, m) => s + (z === "total" ? m.total : m[z].revenue), 0);
  const cnt = (z) => monthly.reduce((s, m) => s + (z === "total" ? m.orders : m[z].orders), 0);
  return (
    <div data-testid="revenue-calendar-table">
      <div className="flex items-center gap-2 mb-2">
        <label className="text-xs text-gray-600">Anno</label>
        <select value={year} onChange={(e) => onYear(Number(e.target.value))} className="text-xs border rounded px-2 py-1 bg-white" data-testid="revenue-calendar-year">
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <span className="text-xs text-gray-400">Gennaio → Dicembre (fuso Europa/Roma)</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-gray-500 border-b">
              <th className="text-left py-1 pr-2">Mese</th>
              {ZONES.map(([z, l, cls]) => <th key={z} className={`text-right py-1 px-2 font-semibold ${cls}`}>{l.replace(" (esclusa Italia)", "")}</th>)}
              <th className="text-right py-1 pl-2 font-bold text-gray-900">Totale</th>
            </tr>
          </thead>
          <tbody>
            {monthly.map((m) => (
              <tr key={m.month} className={`border-b border-gray-100 ${m.total ? "" : "text-gray-400"}`} data-testid={`revenue-calendar-month-${m.month}`}>
                <td className="py-1 pr-2 font-medium">{MONTHS[m.month - 1]}</td>
                {ZONES.map(([z]) => (
                  <td key={z} className="text-right py-1 px-2 whitespace-nowrap" data-testid={`revenue-calendar-${m.month}-${z}`}>
                    {m[z].revenue ? <>{eur(m[z].revenue)} <span className="text-gray-400">({m[z].orders})</span></> : "—"}
                  </td>
                ))}
                <td className="text-right py-1 pl-2 font-bold text-gray-900 whitespace-nowrap" data-testid={`revenue-calendar-${m.month}-total`}>{m.total ? eur(m.total) : "—"}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-gray-300 font-bold text-gray-900" data-testid="revenue-calendar-year-total">
              <td className="py-1.5 pr-2">Totale {year}</td>
              {ZONES.map(([z]) => <td key={z} className="text-right py-1.5 px-2 whitespace-nowrap">{eur(sum(z))} <span className="text-gray-400 font-normal">({cnt(z)})</span></td>)}
              <td className="text-right py-1.5 pl-2 whitespace-nowrap" data-testid="revenue-calendar-grand-total">{eur(sum("total"))} <span className="text-gray-400 font-normal">({cnt("total")})</span></td>
            </tr>
          </tfoot>
        </table>
      </div>
      <p className="text-[11px] text-gray-400 mt-1">Tra parentesi il numero di ordini.</p>
    </div>
  );
};

/** Dashboard: revenue split Italia / EU / extra-EU over 1, 3, 12 months. */
export const RevenueByZoneWidget = () => {
  const [data, setData] = useState(null);
  const [countries, setCountries] = useState({});
  const [period, setPeriod] = useState("month");
  const [showCountries, setShowCountries] = useState(true);
  const [year, setYear] = useState(null);
  const [years, setYears] = useState([]);
  const [monthly, setMonthly] = useState([]);

  useEffect(() => {
    axios.get(`${API}/dashboard/revenue-by-zone`, { params: year ? { year } : {}, headers: { Authorization: `Bearer ${sessionStorage.getItem("access_token")}` } })
      .then((r) => { setData(r.data.periods); setCountries(r.data.countries || {}); setMonthly(r.data.monthly || []); setYears(r.data.years || []); if (!year) setYear(r.data.year); })
      .catch(() => setData({}));
  }, [year]);

  const rows = data?.[period] || {};
  const total = ZONES.reduce((s, [z]) => s + (rows[z]?.revenue || 0), 0);

  return (
    <Card data-testid="revenue-zone-widget">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <CardTitle className="text-base flex items-center gap-2"><Globe2 className="w-4 h-4 text-blue-500" /> Incasso per area geografica</CardTitle>
          <div className="flex gap-1" data-testid="revenue-zone-periods">
            {PERIODS.map(([k, l]) => (
              <button key={k} type="button" onClick={() => setPeriod(k)} data-testid={`revenue-zone-period-${k}`}
                className={`text-xs px-2.5 py-1 rounded-full border ${period === k ? "bg-gray-900 text-white border-gray-900" : "text-gray-600 hover:bg-gray-50"}`}>{l}</button>
            ))}
          </div>
        </div>
        <p className="text-xs text-gray-500">Ordini pagati o in lavorazione (esclusi annullati e in attesa di pagamento). <strong>UE</strong> = solo i 27 Stati membri (fiscale): Regno Unito, Svizzera, Norvegia, Bielorussia ecc. contano come Extra UE.</p>
      </CardHeader>
      <CardContent>
        {data === null ? <p className="text-sm text-gray-400">Caricamento…</p> : period === "calendar" ? (
          <CalendarYearTable monthly={monthly} year={year} years={years} onYear={setYear} />
        ) : (
          <div className="space-y-2">
            {ZONES.map(([z, label, cls]) => {
              const r = rows[z] || { revenue: 0, orders: 0 };
              const pct = total ? Math.round((r.revenue / total) * 100) : 0;
              return (
                <div key={z} data-testid={`revenue-zone-${z}`}>
                  <div className="flex justify-between text-sm">
                    <span className={`font-medium ${cls}`}>{label}</span>
                    <span className="font-bold text-gray-900" data-testid={`revenue-zone-${z}-amount`}>{eur(r.revenue)} <span className="text-xs text-gray-500 font-normal">· {r.orders} ordini · {pct}%</span></span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full mt-1"><div className="h-2 rounded-full bg-gradient-to-r from-pink-500 to-fuchsia-600" style={{ width: `${pct}%` }} /></div>
                </div>
              );
            })}
            <div className="flex justify-between text-sm border-t pt-2 mt-2">
              <span className="text-gray-600">Totale periodo</span>
              <span className="font-extrabold text-gray-900" data-testid="revenue-zone-total">{eur(total)}</span>
            </div>
            <div className="border-t pt-2 mt-2">
              <button type="button" onClick={() => setShowCountries((v) => !v)} className="text-xs font-semibold text-gray-600 hover:text-gray-900" data-testid="revenue-countries-toggle">
                {showCountries ? "▾" : "▸"} Classifica paesi ({(countries[period] || []).length})
              </button>
              {showCountries && (
                <ol className="mt-2 space-y-1" data-testid="revenue-countries-list">
                  {(countries[period] || []).length === 0 && <li className="text-xs text-gray-400">Nessun ordine nel periodo</li>}
                  {(countries[period] || []).slice(0, 12).map((c, i) => (
                    <li key={c.country} className="flex items-center gap-2 text-sm" data-testid={`revenue-country-${i}`}>
                      <span className="w-5 text-xs text-gray-400 text-right">{i + 1}.</span>
                      <span className="flex-1 text-gray-800 truncate">{c.country} <span className="text-[10px] uppercase text-gray-400 ml-1">{c.zone === "italia" ? "IT" : c.zone === "ue" ? "UE" : "Extra"}</span></span>
                      <span className="text-xs text-gray-500">{c.orders} {c.orders === 1 ? "ordine" : "ordini"}</span>
                      <span className="font-bold text-gray-900 w-24 text-right">{eur(c.revenue)}</span>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
